import { SVG_NAMESPACE } from "../constants/strings";
import { SharkSpecies } from "./shark-species";
import { DEFAULT_SVG_SIZE } from "../constants/constants";
import { SharkTreeNode } from "./shark-tree-node";
import { Svg } from "../drawing/svg";
import { Utils } from "../utils/utils";
import { BLACK, DEFAULT_NODE_COLOR, DEFAULT_PATH_COLOR, PULSING_NODE_COLOR, TAXONOMIC_LEVEL_COLORS, WHITE } from "../constants/colors";
import { CONSERVATION_STATUS, REPRODUCTIVE_STRATEGY, TEMPERATURE_REGULATION, FEEDING_BEHAVIOR, OCEAN_ZONE, GEOGRAPHICAL_DISTRIBUTION, HABITAT, WATER_COLUMN, PHYSICAL_CHARACTERISTIC, BEHAVIOR, NUM_GILLS, NUM_DORSAL_FINS, ANAL_FIN, NICTITATING_MEMBRANE, CAUDAL_FIN_SHAPE, MOUTH_IN_FRONT_OF_EYES, BIOLUMINESCENT, DORSAL_FIN_SPINES, SPIRACLES, FLATTENED_BODY, GROUP_BEHAVIOR, TAXONOMIC_LEVELS, PROXIMAL_DORSAL_FINS, SPECIES_TYPE, TAIL_SPINES, ELECTRIC_ORGAN, VENOMOUS_SPINE, OPERCULUM, SNOUT_SHAPE, DEPTH_ZONE } from "../constants/enums";
import { DEFAULT_DASH_PATTERN, DEFAULT_SOLID_PATTERN, DEFAULT_STROKE_OPACITY, DEFAULT_STROKE_WIDTH, HIGHLIGHTED_STROKE_OPACITY, HIGHLIGHTED_STROKE_WIDTH, TAXONOMIC_THICKNESS } from "../constants/style";
export class SharkTree {
    constructor(sharkTreeConfig, containerWidth, speciesType = SPECIES_TYPE.SHARKS) {
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "root", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "speciesType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "centerX", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "centerY", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "radius", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "svgSize", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "levelHeight", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "maxDepth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentNode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "currentSharkIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeTaxonomicLevel", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeTaxonomicValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "taxonomicLevels", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeTagCategory", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "activeTagValue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "tagCategories", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.config = sharkTreeConfig;
        this.root = new SharkTreeNode(this.config, null, speciesType);
        this.speciesType = speciesType;
        this.svgSize = containerWidth > 0 ? Math.min(containerWidth, window.innerHeight) : DEFAULT_SVG_SIZE;
        this.centerX = this.svgSize / 2;
        this.centerY = this.svgSize / 2;
        this.radius = this.svgSize / 3;
        this.maxDepth = this.getMaxDepth();
        this.levelHeight = this.radius / this.maxDepth;
        this.currentNode = this.root;
        this.currentSharkIndex = 0;
        this.activeTaxonomicLevel = null;
        this.activeTaxonomicValue = null;
        this.taxonomicLevels = new Map();
        this.initializeTaxonomicLevels();
        this.activeTagCategory = null;
        this.activeTagValue = null;
        this.tagCategories = new Map();
        this.initializeTagCategories();
    }
    /*----------------------------------------|
    |                GETTERS                  |
    |----------------------------------------*/
    getSharkSpeciesList(root = this.root) {
        let sharkSpecies = [];
        root.children.forEach((child) => {
            if (child instanceof SharkTreeNode)
                sharkSpecies = [...sharkSpecies, ...this.getSharkSpeciesList(child)];
            else
                sharkSpecies.push(child);
        });
        return sharkSpecies;
    }
    getMaxDepth(root = this.root) {
        if (!root)
            return 0;
        if (root.children.length === 0)
            return 1;
        let maxChildDepth = 0;
        root.children.forEach((child) => {
            const childDepth = (child instanceof SharkSpecies) ?
                1 :
                this.getMaxDepth(child);
            if (childDepth > maxChildDepth) {
                maxChildDepth = childDepth;
            }
        });
        return maxChildDepth + 1;
    }
    getNumMergeParters(index, sharkTreeStack, hashChains) {
        const parentHash = sharkTreeStack[index].getParentHash();
        if (!parentHash)
            return 0;
        const bitArray = hashChains.map((hashChain) => hashChain.includes(parentHash) ? 1 : 0);
        return bitArray.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
    }
    getItemSetsToMerge(sharkTreeStack) {
        let index = 0;
        const indicesRemoved = [];
        let itemsInSet = [];
        const itemSetsToMerge = [];
        const hashChains = sharkTreeStack.map((item) => item.getHashChain());
        while (index < sharkTreeStack.length) {
            const item = sharkTreeStack[index];
            if (this.readyToMerge(index, sharkTreeStack, hashChains)) {
                const numMergeParters = this.getNumMergeParters(index, sharkTreeStack, hashChains);
                if (numMergeParters > 1) {
                    itemsInSet.push(item);
                    indicesRemoved.push(index);
                    if (itemsInSet.length == numMergeParters) {
                        itemSetsToMerge.push(itemsInSet);
                        itemsInSet = [];
                    }
                }
            }
            else {
                itemsInSet = [];
            }
            index++;
        }
        return [itemSetsToMerge, indicesRemoved];
    }
    /*----------------------------------------|
    |                DRAWING                  |
    |----------------------------------------*/
    draw() {
        const svg = document.createElementNS(SVG_NAMESPACE, "svg");
        svg.setAttribute("width", this.svgSize.toString());
        svg.setAttribute("height", this.svgSize.toString());
        svg.setAttribute("viewBox", `0 0 ${this.svgSize} ${this.svgSize}`);
        svg.setAttribute("opacity", "1.0"); // Single opacity for entire SVG
        // Add solid background
        const bg = document.createElementNS(SVG_NAMESPACE, "rect");
        bg.setAttribute("x", "0");
        bg.setAttribute("y", "0");
        bg.setAttribute("width", this.svgSize.toString());
        bg.setAttribute("height", this.svgSize.toString());
        // bg.setAttribute("fill", "rgba(245, 245, 245, 1.0)"); // Subtle grey, full opacity
        bg.setAttribute("fill", WHITE);
        svg.appendChild(bg);
        const g = document.createElementNS(SVG_NAMESPACE, "g");
        g.setAttribute("id", "shark-tree-group");
        svg.appendChild(g);
        const sharkSpecies = this.getSharkSpeciesList();
        this.drawRim(g, svg, sharkSpecies, this.centerX, this.centerY);
        let sharkTreeStack = [...sharkSpecies];
        for (let depth = 0; depth < this.getMaxDepth() - 1; depth++) {
            sharkTreeStack = this.runPipeline(g, sharkTreeStack, depth + 1);
        }
        this.extend(g, sharkTreeStack);
        this.addInteractionListeners(svg, g, sharkSpecies);
        svg.setAttribute("id", "shark-tree");
        this.updateSelection(sharkSpecies[0]);
        return svg;
    }
    drawRim(g, svg, sharkSpecies, centerX, centerY) {
        const outerCircle = Svg.drawCircle(centerX, centerY, this.radius);
        outerCircle.setAttribute("id", "shark-rim");
        outerCircle.setAttribute("stroke", BLACK);
        outerCircle.setAttribute("stroke-width", "2");
        outerCircle.setAttribute("fill", "none");
        g.appendChild(outerCircle);
        const numSpecies = sharkSpecies.length;
        const spacing = (2 * Math.PI) / numSpecies;
        const dot = Svg.drawCircle(centerX, centerY, 5, BLACK);
        dot.setAttribute("stroke", BLACK);
        dot.setAttribute("stroke-width", "1");
        g.appendChild(dot);
        sharkSpecies.forEach((shark, index) => {
            this.drawSharkOnRim(g, shark, index, spacing);
        });
    }
    drawSharkOnRim(g, shark, index, spacing) {
        const angle = index * spacing;
        const x = this.centerX + this.radius * Math.cos(angle);
        const y = this.centerY + this.radius * Math.sin(angle);
        shark.setX(x);
        shark.setY(y);
        shark.setIndex(index);
        const circle = Svg.drawCircle(x, y, 5, BLACK);
        shark.setNode(circle);
        circle.dataset.shark = JSON.stringify(shark.config);
        g.appendChild(circle);
    }
    animateRotation(g, startRotation, endRotation, centerX, centerY) {
        const duration = 500; // Animation duration in milliseconds
        const startTime = performance.now();
        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // Use easing function for smooth animation
            const easeProgress = 1 - Math.pow(1 - progress, 3); // ease-out cubic
            const currentRotation = startRotation + (endRotation - startRotation) * easeProgress;
            g.setAttribute("transform", `rotate(${currentRotation}, ${centerX}, ${centerY})`);
            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        };
        requestAnimationFrame(animate);
    }
    addInteractionListeners(svg, g, sharkSpecies) {
        let viewBox = { x: 0, y: 0, width: this.svgSize, height: this.svgSize };
        let isDragging = false;
        let startX, startY;
        let rotation = 0;
        const style = document.createElementNS(SVG_NAMESPACE, "style");
        style.textContent = `
            .pulse {
                animation: pulse 1s infinite ease-in-out;
            }
            @keyframes pulse {
                0% { r: 5; opacity: 1; }
                50% { r: 10; opacity: 0.7; }
                100% { r: 5; opacity: 1; }
            }
        `;
        svg.appendChild(style);
        sharkSpecies.forEach(shark => {
            const node = shark.getNode();
            node.addEventListener("click", () => {
                const previousShark = sharkSpecies[this.currentSharkIndex];
                this.resetHighlightPath(previousShark, true);
                this.updateSelection(shark);
                window.dispatchEvent(new CustomEvent("select-shark", {
                    detail: { sharkSpecies: shark }
                }));
                // Calculate rotation needed to bring this species to 0 degrees
                const numSpecies = sharkSpecies.length;
                const spacing = 360 / numSpecies;
                const currentAngle = shark.index * spacing;
                const targetRotation = -currentAngle;
                // Animate the rotation smoothly
                this.animateRotation(g, rotation, targetRotation, this.centerX, this.centerY);
                rotation = targetRotation;
                // Update current shark index
                this.currentSharkIndex = shark.index;
                const node = shark.getNode();
                this.highlightPathToShark(shark.binomialName, HIGHLIGHTED_STROKE_WIDTH, DEFAULT_PATH_COLOR, DEFAULT_STROKE_OPACITY, true);
                g.removeChild(node);
                g.appendChild(node);
            });
            node.addEventListener("mouseover", (e) => {
                const tooltip = document.createElement("div");
                tooltip.innerHTML = `${shark.commonName || shark.binomialName}`;
                tooltip.style.position = "absolute";
                tooltip.style.background = "rgba(255, 255, 255, 0.95)";
                tooltip.style.border = `1px solid #00688B`;
                tooltip.style.borderRadius = "5px";
                tooltip.style.padding = "4px 5px";
                tooltip.style.fontFamily = "'Roboto', sans-serif";
                tooltip.style.fontWeight = "550";
                tooltip.style.fontSize = "13px";
                tooltip.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                tooltip.style.left = `${e.pageX + 10}px`;
                tooltip.style.top = `${e.pageY + 10}px`;
                document.body.appendChild(tooltip);
                node.addEventListener("mouseout", () => tooltip.remove(), { once: true });
            });
        });
        svg.addEventListener("dblclick", () => {
            // Reset viewBox to initial state
            viewBox = { x: 0, y: 0, width: this.svgSize, height: this.svgSize };
            this.updateViewBox(svg, viewBox);
            // Reapply any active highlights if needed
            if (this.activeTaxonomicLevel) {
                this.highlightTaxonomicLevel(this.activeTaxonomicLevel, this.activeTaxonomicValue);
            }
            if (this.activeTagCategory) {
                this.highlightTagCategory(this.activeTagCategory, this.activeTagValue);
            }
        });
        svg.addEventListener("wheel", (event) => {
            event.preventDefault();
            const mouseX = event.clientX - svg.getBoundingClientRect().left;
            const mouseY = event.clientY - svg.getBoundingClientRect().top;
            if (event.ctrlKey) {
                const scale = event.deltaY > 0 ? 1.1 : 0.9;
                viewBox.width *= scale;
                viewBox.height *= scale;
                viewBox.x += (mouseX * (1 - scale)) / (this.svgSize / viewBox.width);
                viewBox.y += (mouseY * (1 - scale)) / (this.svgSize / viewBox.height);
                this.updateViewBox(svg, viewBox);
            }
            else {
                const delta = -event.deltaY;
                rotation = (rotation + delta / 10) % 360;
                g.setAttribute("transform", `rotate(${rotation}, ${this.centerX}, ${this.centerY})`);
                const numSpecies = sharkSpecies.length;
                const spacing = 360 / numSpecies;
                let sharkIndex = Math.floor((-rotation / spacing + 1) % numSpecies);
                if (sharkIndex < 0)
                    sharkIndex = numSpecies + sharkIndex;
                if (sharkIndex !== this.currentSharkIndex) {
                    const previousShark = sharkSpecies[this.currentSharkIndex];
                    this.resetHighlightPath(previousShark, true);
                    this.currentSharkIndex = sharkIndex;
                    const currentShark = sharkSpecies[sharkIndex];
                    const node = currentShark.getNode();
                    this.highlightPathToShark(currentShark.binomialName, HIGHLIGHTED_STROKE_WIDTH, DEFAULT_PATH_COLOR, DEFAULT_STROKE_OPACITY, true);
                    g.removeChild(node);
                    g.appendChild(node);
                    this.updateSelection(currentShark);
                    window.dispatchEvent(new CustomEvent("select-shark", {
                        detail: { sharkSpecies: currentShark }
                    }));
                }
            }
        });
        // Pan with drag
        svg.addEventListener("mousedown", (event) => {
            if (!event.ctrlKey) {
                isDragging = true;
                startX = event.clientX;
                startY = event.clientY;
            }
        });
        svg.addEventListener("mousemove", (event) => {
            if (!isDragging)
                return;
            const dx = (event.clientX - startX) * (viewBox.width / this.svgSize);
            const dy = (event.clientY - startY) * (viewBox.height / this.svgSize);
            viewBox.x -= dx;
            viewBox.y -= dy;
            startX = event.clientX;
            startY = event.clientY;
            this.updateViewBox(svg, viewBox);
        });
        svg.addEventListener("mouseup", () => (isDragging = false));
        svg.addEventListener("mouseleave", () => (isDragging = false));
        // Pinch-to-zoom and two-finger pan (touch devices)
        let initialDistance = null;
        let initialCenter = null;
        svg.addEventListener("touchstart", (event) => {
            if (event.touches.length === 2) {
                initialDistance = this.getTouchDistance(event.touches);
                initialCenter = this.getTouchCenter(event.touches, svg);
            }
        });
        svg.addEventListener("touchmove", (event) => {
            if (event.touches.length === 2 && initialDistance && initialCenter) {
                const newDistance = this.getTouchDistance(event.touches);
                const scale = initialDistance / newDistance;
                const newCenter = this.getTouchCenter(event.touches, svg);
                viewBox.width *= scale;
                viewBox.height *= scale;
                viewBox.x += (initialCenter.x * (1 - scale)) / (this.svgSize / viewBox.width);
                viewBox.y += (initialCenter.y * (1 - scale)) / (this.svgSize / viewBox.height);
                const dx = (newCenter.x - initialCenter.x) * (viewBox.width / this.svgSize);
                const dy = (newCenter.y - initialCenter.y) * (viewBox.height / this.svgSize);
                viewBox.x -= dx;
                viewBox.y -= dy;
                this.updateViewBox(svg, viewBox);
                initialDistance = newDistance;
                initialCenter = newCenter;
            }
        });
        svg.addEventListener("touchend", () => {
            initialDistance = null;
            initialCenter = null;
        });
    }
    /*----------------------------------------|
    |                PIPELINE                 |
    |----------------------------------------*/
    runPipeline(svg, sharkTreeStack, iteration) {
        // Extend
        this.extend(svg, sharkTreeStack);
        // Connect
        const indicesRemoved = this.connect(svg, sharkTreeStack, iteration);
        // Cluster
        const newSharkTreeStack = this.getNextStackLayer(sharkTreeStack, indicesRemoved);
        return newSharkTreeStack;
    }
    extend(svg, sharkTreeStack) {
        sharkTreeStack.forEach((item) => {
            var _a, _b;
            const line = item instanceof SharkSpecies ?
                item.getLastParentPathSegment() :
                (item.getParentPath().length ?
                    item.getLastParentPathSegment() :
                    item.getLastChildPathSegment());
            const x = !line && item instanceof SharkSpecies ? item.getX() : parseFloat((_a = Utils.getAttributeFromLineOrPath(line, "x2")) !== null && _a !== void 0 ? _a : "0");
            const y = !line && item instanceof SharkSpecies ? item.getY() : parseFloat((_b = Utils.getAttributeFromLineOrPath(line, "y2")) !== null && _b !== void 0 ? _b : "0");
            const newLine = Svg.extendLineToPoint(x, y, this.centerX, this.centerY, this.levelHeight);
            newLine.setAttribute("stroke", DEFAULT_PATH_COLOR);
            newLine.setAttribute("stroke-linecap", "round");
            item.addParentPathSegment(newLine);
            svg.appendChild(newLine);
        });
    }
    connect(svg, sharkTreeStack, iteration) {
        const [itemSetsToMerge, indicesRemoved] = this.getItemSetsToMerge(sharkTreeStack);
        itemSetsToMerge.forEach((itemSet) => {
            const arcSet = [];
            const numItems = itemSet.length;
            const middleIndex = numItems - 1;
            const lines = itemSet.map((item) => item.getLastParentPathSegment());
            lines.forEach((line, i) => {
                if (i !== lines.length - 1) {
                    const [firstArc, secondArc] = Svg.getArcs(this.centerX, this.centerY, this.radius, this.levelHeight, line, lines[i + 1], iteration);
                    firstArc.setAttribute("stroke", DEFAULT_PATH_COLOR);
                    firstArc.setAttribute("stroke-linecap", "round");
                    secondArc.setAttribute("stroke", DEFAULT_PATH_COLOR);
                    secondArc.setAttribute("stroke-linecap", "round");
                    arcSet.push(firstArc, secondArc);
                }
            });
            for (let i = 0; i < Math.floor(numItems / 2); i++) {
                arcSet.slice(2 * i, middleIndex).forEach((arc) => itemSet[i].addParentPathSegment(arc));
                arcSet.slice(middleIndex, arcSet.length - 2 * i).forEach((arc) => itemSet[numItems - 1 - i].addParentPathSegment(arc));
            }
            itemSet.forEach((item) => item.addParentPathToParentChildPath());
            this.addArcsToSvg(svg, arcSet);
        });
        return indicesRemoved;
    }
    getNextStackLayer(sharkTreeStack, indicesRemoved) {
        const newSharkTreeStack = [];
        const parentHashes = new Set();
        sharkTreeStack.forEach((item, index) => {
            if (!indicesRemoved.includes(index)) {
                newSharkTreeStack.push(item);
            }
            else {
                const parent = item.getParent();
                const parentHash = parent === null || parent === void 0 ? void 0 : parent.getHash();
                if (parent && !parentHashes.has(parentHash)) {
                    parentHashes.add(parentHash);
                    newSharkTreeStack.push(parent);
                }
            }
        });
        return newSharkTreeStack;
    }
    /*----------------------------------------|
    |                HELPERS                  |
    |----------------------------------------*/
    readyToMerge(index, sharkTreeStack, hashChains) {
        const parentHash = sharkTreeStack[index].getParentHash();
        const otherHashChains = hashChains.filter((_hashChains, i) => (i !== index));
        const otherHashes = new Set(otherHashChains.flat());
        return !otherHashes.has(parentHash) || (otherHashes.has(parentHash) && otherHashChains.every((hashChain) => Math.abs(hashChain.indexOf(parentHash)) == 1));
    }
    addArcsToSvg(svg, arcs) {
        arcs.forEach((arc) => svg.appendChild(arc));
    }
    updateSelection(selectedShark) {
        const sharkSpecies = this.getSharkSpeciesList();
        sharkSpecies.forEach(shark => {
            const node = shark.getNode();
            node.setAttribute("fill", DEFAULT_NODE_COLOR);
            node.classList.remove("pulse");
        });
        if (this.activeTaxonomicLevel) {
            const levelData = this.taxonomicLevels.get(this.activeTaxonomicLevel);
            if (levelData) {
                const speciesToHighlight = this.activeTaxonomicValue
                    ? levelData.species.filter(s => s[this.activeTaxonomicLevel] === this.activeTaxonomicValue)
                    : levelData.species;
                speciesToHighlight.forEach(shark => {
                    const node = shark.getNode();
                    node.setAttribute("fill", TAXONOMIC_LEVEL_COLORS[this.activeTaxonomicLevel]);
                });
            }
        }
        if (this.activeTagCategory) {
            const categoryData = this.tagCategories.get(this.activeTagCategory);
            if (categoryData) {
                sharkSpecies.forEach(shark => {
                    shark.setNodeOpacity(HIGHLIGHTED_STROKE_OPACITY);
                });
                const speciesToHighlight = this.activeTagValue
                    ? categoryData.species.filter(s => s.tags.includes(this.activeTagValue))
                    : categoryData.species.filter(s => s.tags.some(tag => this.getTagCategory(tag) === this.activeTagCategory));
                speciesToHighlight.forEach(shark => {
                    shark.setNodeOpacity(DEFAULT_STROKE_OPACITY);
                });
            }
        }
        const selectedSharkNode = selectedShark.getNode();
        selectedSharkNode.setAttribute("fill", PULSING_NODE_COLOR);
        selectedSharkNode.setAttribute("fill-opacity", `${DEFAULT_STROKE_OPACITY}`);
        selectedSharkNode.classList.add("pulse");
    }
    getTouchCenter(touches, svg) {
        const rect = svg.getBoundingClientRect();
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2 - rect.left,
            y: (touches[0].clientY + touches[1].clientY) / 2 - rect.top
        };
    }
    getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    updateViewBox(svg, viewBox) {
        svg.setAttribute("viewBox", `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`);
    }
    getPathStyle(sharksToCheck) {
        let strokeColor = DEFAULT_PATH_COLOR;
        let strokeWidth = `${DEFAULT_STROKE_WIDTH}`;
        let strokeOpacity = `${DEFAULT_STROKE_OPACITY}`;
        let dashPattern = DEFAULT_SOLID_PATTERN;
        if (this.activeTaxonomicLevel) {
            const levelData = this.taxonomicLevels.get(this.activeTaxonomicLevel);
            if (levelData && sharksToCheck.some(s => !this.activeTaxonomicValue || s[this.activeTaxonomicLevel] === this.activeTaxonomicValue)) {
                strokeWidth = levelData.thickness.toString();
                strokeColor = TAXONOMIC_LEVEL_COLORS[this.activeTaxonomicLevel];
            }
        }
        if (this.activeTagCategory) {
            const hasTagInCategory = sharksToCheck.some(s => s.tags.some(tag => this.getTagCategory(tag) === this.activeTagCategory));
            strokeOpacity = `${HIGHLIGHTED_STROKE_OPACITY}`;
            if (hasTagInCategory && (!this.activeTagValue || sharksToCheck.some(s => s.tags.includes(this.activeTagValue)))) {
                dashPattern = DEFAULT_DASH_PATTERN;
                strokeOpacity = `${DEFAULT_STROKE_OPACITY}`;
            }
        }
        return { strokeColor, strokeWidth, strokeOpacity, dashPattern };
    }
    ;
    applyStyle(segments, style) {
        segments.forEach(segment => {
            segment.setAttribute("stroke", style.strokeColor);
            segment.setAttribute("stroke-width", style.strokeWidth);
            segment.setAttribute("stroke-opacity", style.strokeOpacity);
            if (style.dashPattern) {
                segment.setAttribute("stroke-dasharray", style.dashPattern);
            }
            else {
                segment.removeAttribute("stroke-dasharray");
            }
        });
    }
    ;
    /*----------------------------------------|
    |           TAXONOMIC GROUPS              |
    |----------------------------------------*/
    initializeTaxonomicLevels() {
        const levels = Object.values(TAXONOMIC_LEVELS);
        levels.forEach((level) => {
            const thickness = TAXONOMIC_THICKNESS[level] || DEFAULT_STROKE_WIDTH;
            this.taxonomicLevels.set(level, {
                species: [],
                color: DEFAULT_PATH_COLOR,
                thickness: thickness
            });
        });
        const allSpecies = this.getSharkSpeciesList(this.root);
        this.collectTaxonomicData(allSpecies);
    }
    collectTaxonomicData(species) {
        species.forEach(shark => {
            Object.values(TAXONOMIC_LEVELS).forEach(level => {
                const levelData = this.taxonomicLevels.get(level);
                if (levelData && shark[level]) {
                    // Only add species if it hasn't been added yet
                    if (!levelData.species.some(s => s.binomialName === shark.binomialName)) {
                        levelData.species.push(shark);
                    }
                }
            });
        });
    }
    highlightTaxonomicLevel(level, value) {
        this.activeTaxonomicLevel = level;
        this.activeTaxonomicValue = value || null;
        const levelData = this.taxonomicLevels.get(level);
        if (!levelData)
            return;
        const speciesToHighlight = value
            ? levelData.species.filter(s => s[level] === value)
            : levelData.species;
        this.getSharkSpeciesList().forEach(shark => this.resetHighlightPath(shark));
        speciesToHighlight.forEach(shark => {
            if (shark.getNode().getAttribute("fill") !== PULSING_NODE_COLOR)
                shark.highlightNode(DEFAULT_NODE_COLOR);
            shark.highlightParentPath(levelData.thickness, TAXONOMIC_LEVEL_COLORS[this.activeTaxonomicLevel], DEFAULT_STROKE_OPACITY, DEFAULT_SOLID_PATTERN);
        });
        if (this.activeTagCategory) {
            this.highlightTagCategory(this.activeTagCategory, this.activeTagValue);
        }
        speciesToHighlight.forEach(shark => {
            if (shark.getNode().getAttribute("fill") !== PULSING_NODE_COLOR)
                shark.highlightNode(TAXONOMIC_LEVEL_COLORS[this.activeTaxonomicLevel]);
        });
    }
    /*----------------------------------------|
    |            TAG CATEGORIES               |
    |----------------------------------------*/
    initializeTagCategories() {
        const sharedCategories = [
            "conservationStatus",
            "reproductiveStrategy",
            "temperatureRegulation",
            "feedingBehavior",
            "groupBehavior",
            "behavior",
            "numGills",
            "isBioluminescent",
            "hasSpiracles",
            "numDorsalFins",
            "hasFlattenedBody",
            "depthZone"
        ];
        const sharkCategories = [
            "caudalFinShape",
            "hasDorsalFinSpines",
            "hasProximalDorsalFins",
            "analFin",
            "nictitatingMembrane",
            "caudalFinShape",
            "mouthInFrontOfEyes",
        ];
        const rayCategories = [
            "tailSpines",
            "electricOrgan",
            "venomousSpine"
        ];
        const chimaeraCategories = [
            "operculum",
            "snoutShape",
        ];
        const categories = [
            ...sharedCategories,
            ...(this.speciesType === SPECIES_TYPE.SHARKS ? sharkCategories : (this.speciesType === SPECIES_TYPE.RAYS ? rayCategories : chimaeraCategories))
        ];
        categories.forEach((category, _index) => {
            this.tagCategories.set(category, {
                species: []
            });
        });
        const allSpecies = this.getSharkSpeciesList(this.root);
        this.collectTagData(allSpecies);
    }
    collectTagData(species) {
        species.forEach(shark => {
            shark.tags.forEach(tag => {
                const category = this.getTagCategory(tag);
                const categoryData = this.tagCategories.get(category);
                if (categoryData && !categoryData.species.some(s => s.binomialName === shark.binomialName)) {
                    categoryData.species.push(shark);
                }
            });
        });
    }
    getTagCategory(tag) {
        const tagEnums = {
            "conservationStatus": Object.values(CONSERVATION_STATUS),
            "reproductiveStrategy": Object.values(REPRODUCTIVE_STRATEGY),
            "temperatureRegulation": Object.values(TEMPERATURE_REGULATION),
            "feedingBehavior": Object.values(FEEDING_BEHAVIOR),
            "groupBehavior": Object.values(GROUP_BEHAVIOR),
            "behavior": Object.values(BEHAVIOR),
            "oceanZone": Object.values(OCEAN_ZONE),
            "geographicalDistribution": Object.values(GEOGRAPHICAL_DISTRIBUTION),
            "habitat": Object.values(HABITAT),
            "waterColumn": Object.values(WATER_COLUMN),
            "physicalCharacteristic": Object.values(PHYSICAL_CHARACTERISTIC),
            "numGills": Object.values(NUM_GILLS),
            "numDorsalFins": Object.values(NUM_DORSAL_FINS),
            "analFin": Object.values(ANAL_FIN),
            "nictitatingMembrane": Object.values(NICTITATING_MEMBRANE),
            "caudalFinShape": Object.values(CAUDAL_FIN_SHAPE),
            "mouthInFrontOfEyes": Object.values(MOUTH_IN_FRONT_OF_EYES),
            "isBioluminescent": Object.values(BIOLUMINESCENT),
            "hasDorsalFinSpines": Object.values(DORSAL_FIN_SPINES),
            "hasProximalDorsalFins": Object.values(PROXIMAL_DORSAL_FINS),
            "tailSpines": Object.values(TAIL_SPINES),
            "electricOrgan": Object.values(ELECTRIC_ORGAN),
            "venomousSpine": Object.values(VENOMOUS_SPINE),
            "hasSpiracles": Object.values(SPIRACLES),
            "hasFlattenedBody": Object.values(FLATTENED_BODY),
            "operculum": Object.values(OPERCULUM),
            "snoutShape": Object.values(SNOUT_SHAPE),
            "depthZone": Object.values(DEPTH_ZONE),
        };
        for (const [category, values] of Object.entries(tagEnums)) {
            if (values.includes(tag))
                return category;
        }
        return "";
    }
    highlightTagCategory(category, value) {
        this.activeTagCategory = category;
        this.activeTagValue = value || null;
        const categoryData = this.tagCategories.get(category);
        if (!categoryData)
            return;
        const speciesToHighlight = value
            ? categoryData.species.filter(s => s.tags.includes(value))
            : categoryData.species.filter(s => s.tags.some(tag => this.getTagCategory(tag) === category));
        // Reset paths but preserve node colors
        this.getSharkSpeciesList().forEach(shark => this.resetHighlightPath(shark, true));
        this.getSharkSpeciesList().forEach(shark => {
            shark.highlightParentPath(null, null, HIGHLIGHTED_STROKE_OPACITY, null);
            if (shark.getNode().getAttribute("fill") !== PULSING_NODE_COLOR)
                shark.setNodeOpacity(HIGHLIGHTED_STROKE_OPACITY);
        });
        // Highlight paths for tagged species
        speciesToHighlight.forEach(shark => {
            shark.highlightParentPath(null, null, DEFAULT_STROKE_OPACITY, DEFAULT_DASH_PATTERN);
            if (shark.getNode().getAttribute("fill") !== PULSING_NODE_COLOR)
                shark.setNodeOpacity(DEFAULT_STROKE_OPACITY);
        });
    }
    /*----------------------------------------|
    |           PATH HIGHLIGHTING             |
    |----------------------------------------*/
    highlightPathToShark(binomialName, strokeWidth = HIGHLIGHTED_STROKE_WIDTH, strokeColor = DEFAULT_PATH_COLOR, strokeOpacity = DEFAULT_STROKE_OPACITY, preserveNodeColor = false) {
        const sharkSpecies = this.getSharkSpeciesList();
        const shark = sharkSpecies.find((s) => s.binomialName === binomialName);
        if (!shark)
            return;
        // Only override node color if not preserving it
        if (!preserveNodeColor) {
            shark.highlightNode(strokeColor);
        }
        // Determine styling based on active tag category
        let finalStrokeWidth = strokeWidth;
        shark.highlightParentPath(finalStrokeWidth, strokeColor, strokeOpacity, DEFAULT_SOLID_PATTERN);
        let sharkParent = shark.getParent();
        while (sharkParent) {
            sharkParent.highlightParentPath(finalStrokeWidth, strokeColor, strokeOpacity, DEFAULT_SOLID_PATTERN);
            sharkParent = sharkParent.getParent();
        }
    }
    resetHighlightPath(shark, preserveNodeColor = false) {
        const node = shark.getNode();
        if (node && !preserveNodeColor) {
            if (node.getAttribute("fill") != PULSING_NODE_COLOR)
                node.setAttribute("fill", DEFAULT_NODE_COLOR);
        }
        const allSpecies = this.getSharkSpeciesList();
        // Style direct path
        const directStyle = this.getPathStyle([shark]);
        this.applyStyle(shark.getParentPath(), directStyle);
        // Style parent paths
        let sharkParent = shark.getParent();
        while (sharkParent) {
            const relatedSharks = allSpecies.filter(s => {
                let p = s.getParent();
                while (p) {
                    if (p === sharkParent)
                        return true;
                    p = p.getParent();
                }
                return false;
            });
            const parentStyle = this.getPathStyle(relatedSharks);
            this.applyStyle(sharkParent.getParentPath(), parentStyle);
            sharkParent = sharkParent.getParent();
        }
    }
    clearAllTagHighlights() {
        this.activeTagCategory = null;
        this.activeTagValue = null;
        this.getSharkSpeciesList().forEach(shark => {
            this.resetHighlightPath(shark);
            shark.setNodeOpacity(DEFAULT_STROKE_OPACITY);
        });
    }
    clearAllTaxonomicHighlights() {
        this.activeTaxonomicLevel = null;
        this.activeTaxonomicValue = null;
        this.getSharkSpeciesList().forEach(shark => this.resetHighlightPath(shark));
    }
}
